// ONE takeover for "a dropped document is being read, checked and filed"
// (BP-101). It is the Bill Process run (src/supplier-invoices/process), lifted
// out of that module so the Flight Report drop gets the same behaviour
// (H303, Aaron 2026-10-01: "we did this for supplier invoices need to move it
// to flight reports").
//
// What the run looks like, from the moment of the drop:
//   · a bar naming the document with the way back;
//   · the stage rail (StageRail), live — the operator sees which step is
//     working rather than a spinner in the drop zone;
//   · THE DOCUMENT beside the values read from it, for the whole run. A value
//     is checked against the paper, so the paper is on screen whenever a value
//     is (the Flight Report's "Check it" step showed no document at all);
//   · feedback IN PLACE, at the top of the values, not in a toast that fades
//     before it is read.
//
// This component owns the section 20 header, gutter and two-column layout.
// Callers supply actual source, pipeline state and domain controls.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import { detailHeaderButtonSx } from "../components/Buttons";
import { useDocumentModeRecord } from "./DocumentModeBoundary";

import PdfProvenance from "../components/PdfProvenance";
import StageRail from "./StageRail";
import type { StageRailProps } from "./StageRail";

export interface DocumentTakeoverNotice {
  /** Stable key — a notice is replaced or dismissed by it. */
  key: string;
  severity: "info" | "success" | "warning" | "error";
  text: React.ReactNode;
  /** Present → the notice carries a close control. */
  onDismiss?: () => void;
}

export interface DocumentTakeoverProps {
  /** The ACTUALLY-DROPPED file. A PDF renders through PdfProvenance, a photo as
   *  an image; absent or empty (a 0-byte offline stand-in) renders a quiet
   *  "no preview" panel, never somebody else's content. */
  file: File | null;
  /** Record identity, alongside the Close controls. */
  title?: React.ReactNode;
  /** Optional source renderer for CSVs or multiple receipt files. */
  document?: React.ReactNode;
  /** Only closes a record whose domain actions have already saved it. */
  onSaveClose?: () => void;
  canSaveClose?: boolean;
  /** false: no Save & Close at all (a flow with nothing to save, e.g. the public sign page, whose decisions are
   *  their own actions). Default true: the button shows, enabled by canSaveClose. */
  enableSaveClose?: boolean;
  fileName: string | null;
  /** Legacy timing metadata; not displayed in the record UI. */
  droppedAt?: number | null;
  /** Legacy run-clock metadata; not displayed in the record UI. */
  elapsedSeconds?: number | null;
  backLabel: string;
  onBack: () => void;
  /** Optional reset action below the document. */
  onReset?: () => void;
  /** Disable the way back while a write is in flight. */
  busy?: boolean;
  rail: StageRailProps;
  /** In-place feedback, shown at the top of the values. */
  notices?: DocumentTakeoverNotice[];
  /** Values to light up on the document (PdfProvenance). */
  highlights?: string[] | null;
  /** One sweep of every value read, when they land. */
  sweepQueries?: string[];
  /**
   * `panel` (default): the values sit in one bordered, scrolling panel the
   * height of the document — the caller's content brings its own padding.
   * `cards`: the caller renders a stack of cards; the column only scrolls.
   */
  findings?: "panel" | "cards";
  /** The values read, and the operator's controls. */
  children: React.ReactNode;
  /** Legacy caller compatibility. Run receipts are no longer rendered. */
  footer?: React.ReactNode;
}

const DocumentWriteContext = createContext<
  ((id: string, busy: boolean) => void) | null
>(null);

/** Keep Close disabled during nested writes without changing domain state. */
export function useDocumentWriteGuard(busy: boolean): void {
  const register = useContext(DocumentWriteContext);
  const id = useId();
  useEffect(() => {
    register?.(id, busy);
    return () => register?.(id, false);
  }, [busy, id, register]);
}

const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp", ".heic", ".gif"];

/** A photo, not a PDF. Type first, extension second — a phone can hand over a
 *  file with an empty MIME type. */
export const isImageDocument = (file: File): boolean => {
  const type = (file.type || "").toLowerCase();
  if (type.startsWith("image/")) return true;
  if (type === "application/pdf") return false;
  const name = file.name.toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
};

/**
 * Seconds since `startedAt`, ticking until `stoppedAt` is set, then frozen on
 * it. For a caller that does not keep its own run clock. The clock measures
 * the machine's reading, not a person's checking — stop it when the read lands.
 */
export function useElapsedSeconds(
  startedAt: number | null,
  stoppedAt: number | null
): number | null {
  const [now, setNow] = useState(() => Date.now());
  const running = startedAt != null && stoppedAt == null;
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (!running) return undefined;
    setNow(Date.now());
    timer.current = window.setInterval(() => setNow(Date.now()), 100);
    return () => {
      if (timer.current != null) window.clearInterval(timer.current);
    };
  }, [running]);
  if (startedAt == null) return null;
  return Math.max(0, ((stoppedAt ?? now) - startedAt) / 1000);
}

/** The document column: the dropped file itself, whatever it is. */
export function DocumentPane({
  file,
  fileName,
  highlights,
  sweepQueries,
}: Pick<
  DocumentTakeoverProps,
  "file" | "fileName" | "highlights" | "sweepQueries"
>) {
  const image = !!file && file.size > 0 && isImageDocument(file);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  useEffect(() => {
    // jsdom has no createObjectURL; a browser always does.
    if (!image || !file || typeof URL.createObjectURL !== "function") {
      setImageUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file, image]);

  if (!file || file.size === 0) {
    return (
      <div className="dtk-empty" data-testid="dtk-no-preview">
        <div className="dtk-empty__name">{fileName || "The document"}</div>
        <div>No preview of this document.</div>
      </div>
    );
  }
  if (image) {
    return (
      <div className="dtk-image">
        {imageUrl && (
          <img
            src={imageUrl}
            alt={`The dropped document, ${fileName ?? file.name}`}
          />
        )}
      </div>
    );
  }
  return (
    <PdfProvenance
      file={file}
      highlights={highlights ?? null}
      sweep={!!sweepQueries?.length}
      sweepQueries={sweepQueries}
      docLabel={fileName ?? file.name}
    />
  );
}

/** @figma https://www.figma.com/design/XZd3Kyn3b7ziLnELrO4TNk?node-id=1731-9805
 * Shared Context=Record composition. Write boundaries remain in module actions. */
const DocumentTakeover: React.FC<DocumentTakeoverProps> = ({
  file,
  fileName,
  title,
  document,
  backLabel,
  onBack,
  onReset,
  onSaveClose,
  enableSaveClose = true,
  canSaveClose = false,
  busy = false,
  rail,
  notices,
  highlights,
  sweepQueries,
  children,
}) => {
  useDocumentModeRecord();
  const [writes, setWrites] = useState<Record<string, boolean>>({});
  const register = useCallback(
    (id: string, active: boolean) =>
      setWrites((previous) => {
        if (!!previous[id] === active) return previous;
        const next = { ...previous };
        if (active) next[id] = true;
        else delete next[id];
        return next;
      }),
    []
  );
  const writing = busy || Object.values(writes).some(Boolean);
  return (
    <DocumentWriteContext.Provider value={register}>
      <Box
        data-testid="document-takeover"
        sx={{
          fontFamily: "var(--an-font-chrome)",
          color: "var(--an-text)",
          bgcolor: "grey.100",
          // The record replaces module chrome; only the 70px global nav remains.
          minHeight: "calc(100dvh - 70px)",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            minHeight: 70,
            px: { xs: 2, sm: 6 },
            py: 1.5,
            bgcolor: "grey.200",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontSize: 24,
              fontWeight: 300,
              color: "primary.main",
              overflowWrap: "anywhere",
              minWidth: 0,
            }}
          >
            {title ?? fileName ?? "Document"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
            <Button
              variant="contained"
              color="secondary"
              sx={{
                ...detailHeaderButtonSx,
                fontSize: 12,
                fontWeight: 600,
                textTransform: "uppercase",
                bgcolor: "grey.500",
                "&:hover": { bgcolor: "grey.600" },
              }}
              onClick={onBack}
              disabled={writing}
              title={backLabel}
            >
              Close
            </Button>
            {enableSaveClose && (
              <Button
                variant="contained"
                sx={{
                  ...detailHeaderButtonSx,
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
                onClick={onSaveClose ?? onBack}
                disabled={writing || !canSaveClose}
              >
                Save &amp; Close
              </Button>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            px: { xs: 2, sm: 6 },
            py: 6,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <StageRail {...rail} />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0,1fr)",
                md: "minmax(0,0.94fr) minmax(0,1fr)",
              },
              alignItems: "start",
              gap: 3,
            }}
          >
            <Box
              data-testid="dtk-document"
              sx={{
                minWidth: 0,
                bgcolor: "background.paper",
                border: "1px solid var(--an-border)",
                borderRadius: "var(--an-r-card)",
                overflow: "hidden",
                "& .pdfprov-root": { border: 0, borderRadius: 0, p: 2 },
                "& .pdfprov-head__label": {
                  fontFamily: "inherit",
                  fontWeight: 400,
                  fontSize: 14,
                  color: "var(--an-muted-2)",
                },
                "& .pdfprov-body": { borderRadius: 0 },
                "& .dtk-image": { p: 2, overflow: "auto" },
                "& .dtk-image img": {
                  width: "100%",
                  height: "auto",
                  display: "block",
                },
                "& .dtk-empty": {
                  minHeight: 360,
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "text.secondary",
                  overflowWrap: "anywhere",
                },
              }}
            >
              {document ?? (
                <DocumentPane
                  file={file}
                  fileName={fileName}
                  highlights={highlights}
                  sweepQueries={sweepQueries}
                />
              )}
            </Box>
            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                "& > *": { minWidth: 0 },
              }}
            >
              {notices && notices.length > 0 && (
                <Box
                  role="status"
                  sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                >
                  {notices.map((n) => (
                    <Alert
                      key={n.key}
                      severity={n.severity}
                      onClose={n.onDismiss}
                    >
                      {n.text}
                    </Alert>
                  ))}
                </Box>
              )}
              {children}
            </Box>
          </Box>
          {onReset && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={onReset} disabled={writing} color="inherit">
                Reset
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </DocumentWriteContext.Provider>
  );
};

export default DocumentTakeover;
